import "@/index.css";
import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { SubscriptionProvider } from "@/contexts/SubscriptionContext";
import { WorkspaceProvider } from "@/contexts/WorkspaceContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import AppLayout from "@/components/AppLayout";
import CookieBanner from "@/components/CookieBanner";
import { initMetaPixel, onConsentChanged, trackPageView } from "@/lib/metaPixel";

import AdminLayout from "@/pages/admin/AdminLayout";
import AdminOverview from "@/pages/admin/AdminOverview";
import AdminUsers from "@/pages/admin/AdminUsers";
import AdminBroadcast from "@/pages/admin/AdminBroadcast";
import AdminCMS from "@/pages/admin/AdminCMS";
import AdminResources from "@/pages/admin/AdminResources";
import AdminToolFlags from "@/pages/admin/AdminToolFlags";
import AdminToolConfig from "@/pages/admin/AdminToolConfig";
import AdminStrategyOperator from "@/pages/admin/AdminStrategyOperator";
import { AdminMessages, AdminTransactions, AdminBugs } from "@/pages/admin/AdminMisc";
import VerifyEmail from "@/pages/VerifyEmail";

import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import AuthCallback from "@/pages/AuthCallback";
import Onboarding from "@/pages/Onboarding";
import Dashboard from "@/pages/Dashboard";
import DiveBrain from "@/pages/DiveBrain";
import Billing from "@/pages/Billing";
import BillingSuccess from "@/pages/BillingSuccess";
import { Account, Support } from "@/pages/AccountAndSupport";
import Settings from "@/pages/Settings";
import BrandKit from "@/pages/BrandKit";
import CarouselSlides from "@/pages/CarouselSlides";
import Integrations from "@/pages/Integrations";
import StrategyHome from "@/pages/strategy/StrategyHome";
import StrategyOnboarding from "@/pages/strategy/StrategyOnboarding";
import StrategyApprove from "@/pages/strategy/StrategyApprove";
import StrategyCalendar from "@/pages/strategy/StrategyCalendar";
import StrategyAnalytics from "@/pages/strategy/StrategyAnalytics";
import StrategyExport from "@/pages/strategy/StrategyExport";
import Consulting from "@/pages/Consulting";
import { ContentVault, VaultItem } from "@/pages/ContentVault";
import { Downloads, Resource } from "@/pages/Downloads";
import { Terms, Privacy, Disclaimer } from "@/pages/Legal";
import Trust from "@/pages/Trust";
import ProTools from "@/pages/ProTools";

// Settings page: real preferences page (see /app/frontend/src/pages/Settings.jsx).

import CreateReel from "@/pages/tools/CreateReel";
import HookBank from "@/pages/tools/HookBank";
import CaptionRewrite from "@/pages/tools/CaptionRewrite";
import EmulationMode from "@/pages/tools/EmulationMode";
import OneDive from "@/pages/tools/OneDive";
import ContentPlan from "@/pages/tools/ContentPlan";
import ViralCarousel from "@/pages/tools/ViralCarousel";
import EmailCampaign from "@/pages/tools/EmailCampaign";
import EmailNewsletter from "@/pages/tools/EmailNewsletter";
import MetaAds from "@/pages/tools/MetaAds";
import GoogleAds from "@/pages/tools/GoogleAds";
import SalesFunnel from "@/pages/tools/SalesFunnel";
import BrandDeal from "@/pages/tools/BrandDeal";
import SEOBlog from "@/pages/tools/SEOBlog";
import ReviewResponse from "@/pages/tools/ReviewResponse";
import CoursePromo from "@/pages/tools/CoursePromo";
import GearPromo from "@/pages/tools/GearPromo";
import SEOProductPage from "@/pages/tools/SEOProductPage";
import HtmlStudio from "@/pages/tools/HtmlStudio";
import Workspaces from "@/pages/Workspaces";
import Campaigns from "@/pages/Campaigns";
import NewCampaign from "@/pages/NewCampaign";
import CampaignDetail from "@/pages/CampaignDetail";
import Team from "@/pages/Team";
import TeamAccept from "@/pages/TeamAccept";

function PublicOnly({ children }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (user) return <Navigate to="/dashboard" replace />;
  return children;
}

function ScopedProviders({ children }) {
  const { user } = useAuth();
  return (
    <SubscriptionProvider user={user}>
      <WorkspaceProvider user={user}>{children}</WorkspaceProvider>
    </SubscriptionProvider>
  );
}

function AppRouter() {
  const location = useLocation();

  // Meta Pixel: init once on boot (respects consent), re-check whenever the
  // cookie banner changes it, and fire PageView on real SPA navigations only.
  useEffect(() => {
    initMetaPixel();
    const onConsent = () => onConsentChanged();
    window.addEventListener("dms:cookie-consent-changed", onConsent);
    return () => window.removeEventListener("dms:cookie-consent-changed", onConsent);
  }, []);

  useEffect(() => {
    // Fires only when the pathname actually changes and Pixel is initialised.
    trackPageView(location.pathname);
  }, [location.pathname]);

  // Emergent OAuth returns with session_id in the URL fragment; on some mobile
  // browsers it can land in the query string instead. Handle both so the
  // AuthCallback exchange always runs before any protected route.
  if (
    location.hash?.includes("session_id=") ||
    location.search?.includes("session_id=")
  ) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<PublicOnly><Login /></PublicOnly>} />
      <Route path="/register" element={<PublicOnly><Register /></PublicOnly>} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/verify-email" element={<VerifyEmail />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/consulting" element={<Consulting />} />
      <Route path="/legal/terms" element={<Terms />} />
      <Route path="/legal/privacy" element={<Privacy />} />
      <Route path="/legal/disclaimer" element={<Disclaimer />} />
      <Route path="/trust" element={<Trust />} />
      <Route path="/pro-tools" element={<ProTools />} />
      <Route path="/downloads" element={<Downloads />} />
      <Route path="/downloads/:slug" element={<Resource />} />
      <Route path="/team/accept" element={<TeamAccept />} />

      <Route path="/onboarding" element={<ProtectedRoute requireOnboarded={false}><Onboarding /></ProtectedRoute>} />
      <Route path="/billing/success" element={<ProtectedRoute requireOnboarded={false}><BillingSuccess /></ProtectedRoute>} />

      {/* Admin */}
      <Route element={<ProtectedRoute requireOnboarded={false}><AdminLayout /></ProtectedRoute>}>
        <Route path="/admin" element={<AdminOverview />} />
        <Route path="/admin/users" element={<AdminUsers />} />
        <Route path="/admin/broadcast" element={<AdminBroadcast />} />
        <Route path="/admin/cms" element={<AdminCMS />} />
        <Route path="/admin/resources" element={<AdminResources />} />
        <Route path="/admin/tools" element={<AdminToolFlags />} />
        <Route path="/admin/tool-config" element={<AdminToolConfig />} />
        <Route path="/admin/strategy-operator" element={<AdminStrategyOperator />} />
        <Route path="/admin/messages" element={<AdminMessages />} />
        <Route path="/admin/bugs" element={<AdminBugs />} />
        <Route path="/admin/transactions" element={<AdminTransactions />} />
      </Route>
      <Route element={<ProtectedRoute><AppLayout /></ProtectedRoute>}>
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/vault" element={<ContentVault />} />
        <Route path="/vault/:id" element={<VaultItem />} />
        <Route path="/account" element={<Account />} />
        <Route path="/billing" element={<Billing />} />
        <Route path="/settings" element={<Settings />} />
        <Route path="/settings/brand-kit" element={<BrandKit />} />
        <Route path="/tools/viral-carousel/slides/:vaultId" element={<CarouselSlides />} />
        <Route path="/integrations" element={<Integrations />} />
        <Route path="/strategy" element={<StrategyHome />} />
        <Route path="/strategy/onboarding" element={<StrategyOnboarding />} />
        <Route path="/strategy/approve" element={<StrategyApprove />} />
        <Route path="/strategy/calendar" element={<StrategyCalendar />} />
        <Route path="/strategy/analytics" element={<StrategyAnalytics />} />
        <Route path="/strategy/export" element={<StrategyExport />} />
        <Route path="/workspaces" element={<Workspaces />} />
        <Route path="/team" element={<Team />} />
        <Route path="/campaigns" element={<Campaigns />} />
        <Route path="/campaigns/new" element={<NewCampaign />} />
        <Route path="/campaigns/:id" element={<CampaignDetail />} />
        <Route path="/support" element={<Support />} />
        <Route path="/dive-brain" element={<DiveBrain />} />

        <Route path="/tools/reel" element={<CreateReel />} />
        <Route path="/tools/hook-bank" element={<HookBank />} />
        <Route path="/tools/caption-rewrite" element={<CaptionRewrite />} />
        <Route path="/tools/emulation" element={<EmulationMode />} />
        <Route path="/tools/one-dive" element={<OneDive />} />
        <Route path="/tools/content-plan" element={<ContentPlan />} />
        <Route path="/tools/viral-carousel" element={<ViralCarousel />} />
        <Route path="/tools/email-campaign" element={<EmailCampaign />} />
        <Route path="/tools/email-newsletter" element={<EmailNewsletter />} />
        <Route path="/tools/meta-ads" element={<MetaAds />} />
        <Route path="/tools/google-ads" element={<GoogleAds />} />
        <Route path="/tools/sales-funnel" element={<SalesFunnel />} />
        <Route path="/tools/brand-deal" element={<BrandDeal />} />
        <Route path="/tools/seo-blog" element={<SEOBlog />} />
        <Route path="/tools/review-response" element={<ReviewResponse />} />
        <Route path="/tools/course-promo" element={<CoursePromo />} />
        <Route path="/tools/gear-promo" element={<GearPromo />} />
        <Route path="/tools/seo-product-page" element={<SEOProductPage />} />
        <Route path="/tools/html-studio" element={<HtmlStudio />} />
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <ScopedProviders>
          <AppRouter />
          <CookieBanner />
          <Toaster position="top-right" richColors closeButton />
        </ScopedProviders>
      </AuthProvider>
    </BrowserRouter>
  );
}
